import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarBlank, ListBullets, NotePencil } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useUrlTab } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import {
  ColumnFilterValue,
  isFilterValueActive,
  matchesColumnFilter,
  mergeWhere,
} from '@/lib/table-query';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { UserOvertimePanel } from '@/modules/time-attendance/overtime';
import { AttendanceMonthlyGrid } from '@/modules/time-attendance/team-attendance/components/attendance-monthly-grid/attendance-monthly-grid';
import { ClockWidget } from '../../components/clock-widget/clock-widget';
import { AttendanceDaysTable } from '../../components/attendance-days-table/attendance-days-table';
import { createCorrectionRequestsTableColumns } from '../../components/correction-requests-table/correction-requests-table-columns';
import { CorrectionFormDialog } from '../../components/correction-form/correction-form-dialog';
import {
  useCurrentUserId,
  useGetMyAttendanceCorrections,
  useMyPendingCorrectionCount,
} from '../../hooks/use-attendance';
import { useAttendancePolicy } from '../../hooks/use-attendance-policy';
import { DEFAULT_ATTENDANCE_CORRECTIONS_ORDER } from '../../graphql/queries';
import { AttendanceDayRow } from '../../services/attendance-days';
import { canTakeLunch, isOpenRecord } from '../../services/attendance-rules';
import { AttendanceCorrectionsPagingResult, AttendanceRecord } from '../../types/attendance.types';

type AttendanceViewMode = 'list' | 'calendar';

/** The employee's last picked view (list or calendar), kept in this browser. */
const VIEW_MODE_STORAGE_KEY = 'hrm.my-attendance.view';

const readViewMode = (): AttendanceViewMode => {
  try {
    return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'calendar' ? 'calendar' : 'list';
  } catch {
    return 'list';
  }
};

const writeViewMode = (mode: AttendanceViewMode): void => {
  try {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Blocked storage (e.g. Safari private mode) — the choice just isn't remembered.
  }
};

/** Every day of the employee's own attendance (list) or one month of it (calendar). */
const AttendanceTab = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const canRequestCorrection = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.ATTENDANCE_REQUEST_CORRECTION);
  const hasOvertime = useHasPermission(HRM_PERMISSIONS.PEOPLE.OVERTIME_ENABLED);
  // Break time only for employees who can take breaks or lunch, like the clock widget.
  const policy = useAttendancePolicy();
  const hasBreaks = policy.canTakeBreak || canTakeLunch(policy);
  // The record to correct, or the day of a missed working day (the form asks for the date without one).
  const [correctionState, setCorrectionState] = useState<{
    open: boolean;
    attendance: AttendanceRecord | null;
    day?: string;
  }>({ open: false, attendance: null });
  const [viewMode, setViewMode] = useState<AttendanceViewMode>(readViewMode);

  const changeViewMode = (mode: AttendanceViewMode) => {
    setViewMode(mode);
    writeViewMode(mode);
  };

  const requestCorrection = useCallback(
    (row: AttendanceDayRow<AttendanceRecord>) =>
      setCorrectionState({ open: true, attendance: row.record, day: row.AttendanceDate }),
    []
  );

  const actions = (
    <div className="flex items-center gap-2">
      {canRequestCorrection && (
        <Button
          variant="outline"
          title={t('REQUEST_CORRECTION')}
          aria-label={t('REQUEST_CORRECTION')}
          className="max-md:size-9 max-md:p-0"
          onClick={() => setCorrectionState({ open: true, attendance: null })}
        >
          <NotePencil className="size-5" />
          {/* On a phone only the icon shows, as the old portal's mobile action buttons. */}
          <span className="max-md:sr-only">{t('REQUEST_CORRECTION')}</span>
        </Button>
      )}
      <Button
        variant={viewMode === 'list' ? 'default' : 'outline'}
        size="icon"
        aria-label={t('LIST_VIEW')}
        title={t('LIST_VIEW')}
        aria-pressed={viewMode === 'list'}
        onClick={() => changeViewMode('list')}
      >
        <ListBullets className="size-5" />
      </Button>
      <Button
        variant={viewMode === 'calendar' ? 'default' : 'outline'}
        size="icon"
        aria-label={t('CALENDAR_VIEW')}
        title={t('CALENDAR_VIEW')}
        aria-pressed={viewMode === 'calendar'}
        onClick={() => changeViewMode('calendar')}
      >
        <CalendarBlank className="size-5" />
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* The list filters from its column headers; the calendar pages by month. */}
      {viewMode === 'list' ? (
        <AttendanceDaysTable
          userId={userId}
          showBreak={hasBreaks}
          showOvertime={hasOvertime}
          onRequestCorrection={canRequestCorrection ? requestCorrection : undefined}
          actions={actions}
        />
      ) : (
        <>
          <div className="flex justify-end">{actions}</div>
          <AttendanceMonthlyGrid
            userId={userId}
            onRequestCorrection={canRequestCorrection ? requestCorrection : undefined}
          />
        </>
      )}

      {/* Without a day (the toolbar button) the form asks for the date itself. */}
      <CorrectionFormDialog
        open={correctionState.open}
        onOpenChange={(open) =>
          setCorrectionState((prev) => (open ? prev : { open: false, attendance: null }))
        }
        correctionDate={correctionState.attendance?.AttendanceDate ?? correctionState.day}
        existingStartTime={correctionState.attendance?.StartTimeUtc}
        // A day clocked in again keeps its first session's end: don't offer it as the clock-out.
        existingEndTime={
          correctionState.attendance && !isOpenRecord(correctionState.attendance)
            ? correctionState.attendance.EndTimeUtc
            : undefined
        }
        existingBreakTimes={correctionState.attendance?.BreakTimeDetails}
      />
    </div>
  );
};

/** Enough for all of one employee's correction requests, fetched at once while filtering by time. */
const ALL_CORRECTIONS_PAGE_SIZE = 1000;

const CorrectionRequestsTab = () => {
  const { t } = useTranslation();
  // A notification's link lists only its correction.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_ATTENDANCE_CORRECTIONS_ORDER,
    linkedId,
  });
  // The server can't read a moment's time of day, and the clock-in/out filters have no day range
  // (`buildColumnFiltersWhere` leaves them out): with one set, all of the employee's requests are
  // fetched and those two filters and the paging run here.
  const timeFilters = tableProps.columnFilters.filter(
    ({ value }) =>
      (value as ColumnFilterValue | undefined)?.type === 'time' &&
      isFilterValueActive(value as ColumnFilterValue)
  );
  const isTimeFiltered = timeFilters.length > 0;

  const { data, isLoading, error } = useGetMyAttendanceCorrections({
    pageNo: isTimeFiltered ? 1 : pagination.pageIndex + 1,
    pageSize: isTimeFiltered ? ALL_CORRECTIONS_PAGE_SIZE : pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { AttendanceCorrections?: AttendanceCorrectionsPagingResult })
    ?.AttendanceCorrections;
  const fetched = result?.items ?? [];
  const matching = isTimeFiltered
    ? fetched.filter((row) =>
        timeFilters.every(({ id, value }) =>
          matchesColumnFilter(row[id as 'StartTimeUtc' | 'EndTimeUtc'], value as ColumnFilterValue)
        )
      )
    : fetched;
  const start = pagination.pageIndex * pagination.pageSize;
  const items = isTimeFiltered ? matching.slice(start, start + pagination.pageSize) : fetched;
  const totalCount = isTimeFiltered ? matching.length : (result?.totalCount ?? 0);

  const columns = createCorrectionRequestsTableColumns({ t });

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />
    </div>
  );
};

const MY_ATTENDANCE_TABS = ['attendance', 'corrections', 'overtime'] as const;

export const MyAttendancePage = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  // Own overtime balance and ledger, for employees overtime is on for (legacy: DE/CH/XK only).
  const hasOvertime = useHasPermission(HRM_PERMISSIONS.PEOPLE.OVERTIME_ENABLED);
  // The tab lives in the URL (Component Map › Page tabs); a notification about a correction opens it.
  const [tab, setTab] = useUrlTab(
    hasOvertime ? MY_ATTENDANCE_TABS : MY_ATTENDANCE_TABS.slice(0, 2),
    'attendance'
  );
  const pendingCorrections = useMyPendingCorrectionCount();

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('ATTENDANCE')} />

      <ClockWidget />

      <Tabs value={tab} onValueChange={setTab}>
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          <TabsTrigger value="attendance" className="max-sm:flex-1">
            {t('ATTENDANCE')}
          </TabsTrigger>
          <TabsTrigger value="corrections" className="max-sm:flex-1" count={pendingCorrections}>
            {t('CORRECTION_REQUESTS')}
          </TabsTrigger>
          {hasOvertime && (
            <TabsTrigger value="overtime" className="max-sm:flex-1">
              {t('OVERTIME_HOURS')}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="attendance">
          <AttendanceTab />
        </TabsContent>
        <TabsContent value="corrections">
          <CorrectionRequestsTab />
        </TabsContent>
        {hasOvertime && (
          <TabsContent value="overtime">
            <UserOvertimePanel userId={userId} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
